// IconTextField.jsx
import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Avatar,
  Divider,
  Stack,
  InputLabel,
  TextField,
  InputAdornment, 
  IconButton,
  Popover, 
  Typography,
} from "@mui/material";
import Visibility     from "@mui/icons-material/Visibility";
import VisibilityOff  from "@mui/icons-material/VisibilityOff";

function IconTextField({
  id,
  label,
  placeholder = "",
  icon,
  avatarVariant = "rounded",
  showDivider = true,
  showPassword = false,  
  value= "",
  onChange = () => {},           // ← default to no-op functions
  onClick={},
  sx: textFieldSx,       // custom sx for the inner TextField
  errors = {},            // ← NEW: the whole error map
  showErrors = false,
  submitTick = 0,  
  loading,
  ...tfProps             // everything else → TextField
}) {

  const isPasswordField =
    tfProps.inputMode === "password" ||
    id?.toLowerCase().includes("password");


      const fieldError = errors[id];         // e.g. "Enter a valid e-mail"
  const hasError   = showErrors && Boolean(fieldError);

  



const [anchorEl, setAnchorEl] = useState(null);
const inputRef = useRef(null); 




 useEffect(() => {
   if (hasError && document.activeElement !== inputRef.current) {
     setAnchorEl(inputRef.current);   // re-open
   } else {
     setAnchorEl(null);               // keep closed
   }
 }, [hasError, submitTick]);          // ← RE-RUN after every submit


   const handleFocus = (e) => {
    setAnchorEl(null);
    tfProps.onFocus?.(e);
  };






  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: 1,
        borderColor: "#C5D3DD",
        borderRadius: "16px",
        px: { xs: 2, sm: 3 },
        gap: { xs: 1.5, sm: 2.5 },
        height: "65px",
        backgroundColor:'#FFFFFF'
      }}
    >
      {/* Icon slot */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {typeof icon === "string" ? (
          <Avatar
            src={icon}
            alt=""
            sx={{ width: 35, height: 35 }}
            variant={avatarVariant}
          />
        ) : (
          icon /* Render any React node (e.g. <SvgIcon/>) */
        )}
      </Box>

      {/* Thin vertical divider */}
      {showDivider && (
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: { xs: .5, sm: .5 }, borderColor: "#C5D3DD", my: 0,     borderRightWidth: 1,   }}
        />
      )}

      {/* Label + TextField */}
      <Stack sx={{ flexGrow: 1, pt: 1, py: 1.5 }}>
        <InputLabel
          htmlFor={id}
          sx={{
            fontFamily: "'Lato', sans-serif",
            fontWeight: 500,
            color: "#BCBCBC",
            fontSize: "14px",
            lineHeight: 1,
            mb: 0.5,
            pt:1,
          }}
        >
          {label}
        </InputLabel>

<TextField
  id={id}
  variant="standard"
  fullWidth
  placeholder={placeholder}
  value={value}
  onChange={onChange}
          error={hasError}   
          inputRef={inputRef}    
 onFocus={handleFocus}
 disabled={loading}  // disable if loading
                 



  /* Only set type if it’s a password field */
  type={isPasswordField ? (showPassword ? "text" : "password") : undefined}

  InputProps={{
    disableUnderline: true,
    ...(tfProps.InputProps || {}),

    /* Inject the adornment only for password fields */
    ...(isPasswordField && {
      endAdornment: (
        <InputAdornment position="end" sx={{ mr: .5 }}>
          <IconButton
            edge="end"
            size="small"
            onClick={onClick}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }),
  }}

  InputLabelProps={{ shrink: false }}
  inputProps={{
    autoCapitalize: 'none', autoCorrect: 'off', 
    sx: { fontSize: "1rem", fontWeight: 600, fontFamily: "'Lato', sans-serif", color: "#49768C",  },
    ...(tfProps.inputProps || {}),
  }}
  sx={{
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
    ...textFieldSx,
  }}
  {...tfProps}
/>


      </Stack>
      <Popover
         open={Boolean(anchorEl)} 
         anchorEl={anchorEl}
         disableAutoFocus
         disableEnforceFocus
         sx={{ pointerEvents: "none" }}            // popover is read-only
         anchorOrigin={{ vertical: "top",    horizontal: "center" }}
         transformOrigin={{ vertical: "bottom", horizontal: "center" }}
         PaperProps={{
           sx: {
                         bgcolor: '#AA5869',

             color: "#fff",
             px: 1.5,
             py: 0.5,
             borderRadius: 1,
             fontSize: 12,
             maxWidth: 260,
           },
           elevation: 3,
         }}
       >
         <Typography variant="caption" sx={{fontFamily: "'Lato', sans-serif",}}>{fieldError}</Typography>
       </Popover>
    </Box>
  );
}

IconTextField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  avatarVariant: PropTypes.oneOf(["rounded", "circular", "square"]),
  showDivider: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  errors: PropTypes.object,
  showErrors: PropTypes.bool, 
  submitTick: PropTypes.number,
  sx: PropTypes.object,
};

export default IconTextField;